import {
  ClientResponse,
  KidsRatioOverviewResponse,
  DayPart,
  EmployeeResponse,
  PlanningEntityIdentifier
} from 'generated';
import { atom } from 'recoil';

import { PlanningEntityMap } from './types';
import { PlanUnitMix } from 'components/types';

import { APNotificationKey } from './utils';

import { EditAppointmentType } from 'RestAPIs/activities/types';
import { DialogType } from './components/DialogManager/types';

export enum View {
  Timeline = 'timeline',
  List = 'list',
  Cards = 'cards',
  CardsPragmatic = 'cards-new',
  Map = 'map',
  Roster = 'roster'
}

const composeAtomKey = (name: string) => `Activity_${name}`;

/*--- Header ---*/
export const planUnitsAtom = atom<PlanUnitMix[]>({
  key: composeAtomKey('planUnits'),
  default: []
});

/*--- UI ---*/
export const isToPlanDrawerOpenAtom = atom<boolean>({
  key: composeAtomKey('isToPlanDrawerOpen'),
  default: true
});

export const selectedGroupingScheduleAtom = atom<string>({
  key: composeAtomKey('selectedGroupingSchedules'),
  default: ''
});

export const dialogActiveAtom = atom<boolean>({
  key: composeAtomKey('dialogActive'),
  default: false
});

export const dialogManagerActiveAtom = atom<{
  type: DialogType | null;
  open: boolean;
  data?: {
    [key: APNotificationKey]: {
      translationKey: string;
      messages: {
        meta: {
          name: string;
          time: string;
        };
      }[];
    };
  };
  onSubmit: () => void;
  onCancel?: () => void;
}>({
  key: composeAtomKey('dialogManagerActive'),
  default: {
    type: null,
    open: false,
    data: {},
    onSubmit: () => undefined,
    onCancel: () => undefined
  }
});

export const dragActionActiveAtom = atom<boolean>({
  key: composeAtomKey('dragActionActiveAtom'),
  default: false
});

export const selectedContextMenuOptionAtom = atom<{
  id: string;
  scheduleStart?: string;
  option: string;
} | null>({
  key: composeAtomKey('selectedContextMenuOption'),
  default: null
});

/*--- Filters ---*/
export const selectedDayPartsAtom = atom<DayPart[]>({
  key: composeAtomKey('dayPartFilter'),
  default: []
});

export const expertiseFilterAtom = atom<string>({
  key: composeAtomKey('expertiseFilter'),
  default: ''
});

export const employeeEntityIdsFilterAtom = atom<string[]>({
  key: composeAtomKey('employeeEntityIdsFilter'),
  default: undefined
});

export const planResponsePlanningEntityIdentifiersAtom = atom<{
  ts: number;
  planningEntityIdentifiers: PlanningEntityIdentifier[];
}>({
  key: composeAtomKey('planResponsePlanningEntityIdentifiers'),
  default: {
    ts: 0,
    planningEntityIdentifiers: []
  }
});

export const selectedPlanningEntitiesAtom = atom<PlanningEntityIdentifier[]>({
  key: composeAtomKey('selectedPlanningEntities'),
  default: []
});

/*--- Planboard ---*/
export const kidsRatioAtom = atom<KidsRatioOverviewResponse | null>({
  key: composeAtomKey('kidsRatio'),
  default: null
});

export const planningAtom = atom<PlanningEntityMap | null>({
  key: composeAtomKey('planning'),
  default: null
});

export const shouldShowPopoverDialogAtom = atom<{
  id: string;
  scheduleStart?: string;
  isUnassignment?: boolean;
  isRouteUnassignment?: boolean;
  isRouteConnect?: boolean;
  isAssignEmployment?: boolean;
  isUnassignAppointment?: boolean;
  isDragAction?: boolean;
}>({
  key: composeAtomKey('shouldShowPopoverDialog'),
  default: {
    id: '',
    scheduleStart: undefined,
    isUnassignment: false,
    isRouteUnassignment: false,
    isUnassignAppointment: false,
    isDragAction: false
  }
});

export const assignEmploymentEditAppointmentTypeAtom = atom<
  EditAppointmentType | null | undefined
>({
  key: composeAtomKey('assignEmploymentEditAppointmentType'),
  default: undefined
});

/*--- Data ---*/
export const clientsAtom = atom<ClientResponse[]>({
  key: composeAtomKey('clients'),
  default: []
});
export const employeesAtom = atom<EmployeeResponse[]>({
  key: composeAtomKey('employees'),
  default: []
});
